// src/pages/FoundingMembers.jsx
import React from "react";
import { Link } from "react-router-dom";
import "../styles/foundingMembers.css";

/**
 * Founding Members page
 * - No countdown
 * - No member photos
 * - No spots remaining / availability UI
 * - No sticky banner
 */

const TOTAL_SPOTS = 100;

const TIERS = [
  {
    name: "Explore",
    price: "$2,199",
    days: "15 days",
    delivery: "Self-pickup",
    path: "/explore",
  },
  {
    name: "Adventure",
    price: "$3,849",
    days: "22 days",
    delivery: "Self-pickup",
    featured: true,
    path: "/adventure",
  },
  {
    name: "Elite",
    price: "$6,599",
    days: "29 days",
    delivery: "Included",
    path: "/elite",
  },
];

const TESTIMONIALS = [
  {
    who: "Member #1 — Arnold Visser, Dorchester",
    quote:
      "Locking in my rate was a no-brainer. But honestly, the VIP event blew me away. This isn’t just a membership — it’s a community.",
  },
  {
    who: "Member #15 — James & Linda K., London",
    quote:
      "We sold our trailer because we weren’t using it enough. Try Play Go is perfect — and the Founding price lock is saving us thousands long-term.",
  },
  {
    who: "Member #23 — Mike T., Cambridge",
    quote:
      "I joined unsure. Best decision ever. I tried different trailers, fell in love with the lifestyle, and now my rate is locked for life.",
  },
];

function GoldSeal() {
  return (
    <div className="fmSeal" aria-hidden="true" title="Founding Member Seal">
      <svg viewBox="0 0 200 200" className="fmSeal__svg">
        <defs>
          <linearGradient id="gold" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="currentColor" stopOpacity="0.95" />
            <stop offset="0.45" stopColor="currentColor" stopOpacity="0.75" />
            <stop offset="1" stopColor="currentColor" stopOpacity="0.95" />
          </linearGradient>
        </defs>
        <g fill="none" stroke="url(#gold)" strokeWidth="6">
          <circle cx="100" cy="100" r="78" />
          <circle cx="100" cy="100" r="64" opacity="0.6" />
        </g>
        <g fill="currentColor" opacity="0.92">
          <path d="M100 46l10 20 22 3-16 15 4 22-20-10-20 10 4-22-16-15 22-3z" />
        </g>
        <text x="100" y="118" textAnchor="middle" className="fmSeal__text">
          FOUNDING
        </text>
        <text x="100" y="142" textAnchor="middle" className="fmSeal__sub">
          MEMBER
        </text>
      </svg>
    </div>
  );
}

export default function FoundingMembers() {
  return (
    <div className="fmPage">
      {/* HERO */}
      <section className="fmHero" id="claim">
        <div className="fmHero__media" aria-hidden="true" />
        <div className="fmHero__overlay" />
        <div className="fmHero__glow" />

        <div className="fmHero__content">
          <div className="fmHero__badgeRow">
            <GoldSeal />
            <div className="fmHero__kicker">LIMITED TO {TOTAL_SPOTS} FOUNDING MEMBERS</div>
          </div>

          <h1 className="fmHero__title">Join the Founding Members Club</h1>
          <h2 className="fmHero__subtitle">
            Lifetime pricing. VIP perks. Permanent status. You’re part of the origin story.
          </h2>

          <p className="fmHero__copy">
            When you launch something revolutionary, you never forget the people who believed in it from day one.
            That’s why we’re creating an elite inner circle — the Founding Members Club, limited to just 100 members.
            This isn’t a promotion. This is permanent, lifetime status as one of the original architects of the
            recreation revolution in Ontario. Once we hit 100, this opportunity closes forever.
          </p>

          <div className="fmHero__cards">
            <div className="fmHeroCard">
              <div className="fmHeroCard__label">Founding Benefits</div>
              <div className="fmHeroList">
                <div className="fmHeroList__item">✓ Lifetime price lock at 2026 rates*</div>
                <div className="fmHeroList__item">✓ VIP Founding event every year</div>
                <div className="fmHeroList__item">✓ Annual prize pool + upgrades</div>
                <div className="fmHeroList__item">✓ Priority booking class & concierge</div>
              </div>
              <div className="fmTiny">Founding status doesn’t cost extra — you choose a tier.</div>
            </div>

            <div className="fmHeroCard">
              <div className="fmHeroCard__label">How to Claim</div>
              <div className="fmHeroSteps">
                <div className="fmHeroSteps__item"><span className="fmStepDot">1</span> Choose your tier</div>
                <div className="fmHeroSteps__item"><span className="fmStepDot">2</span> Complete onboarding</div>
                <div className="fmHeroSteps__item"><span className="fmStepDot">3</span> Receive Founding status</div>
              </div>
              <div className="fmTiny">We’ll confirm everything during your orientation session.</div>
            </div>
          </div>

          <div className="fmHero__ctaRow">
            <a className="fmBtn" href="#tiers">
              Claim Your Founding Member Status
            </a>
            <Link className="fmBtn fmBtn--ghost" to="/clubs">
              View Membership Tiers
            </Link>
          </div>

          <div className="fmHero__fine">
            *Permanent recognition applies while your membership remains active.
          </div>
        </div>
      </section>

      {/* WHAT MAKES IT DIFFERENT */}
      <section className="fmSection fmSection--tight">
        <div className="fmWrap">
          <div className="fmSection__head">
            <h3 className="fmH3">This is where the line gets drawn.</h3>
            <p className="fmP">
              Everyone after you will enjoy Try Play Go. Only the first {TOTAL_SPOTS} will be Founding Members — with
              permanent recognition, priority access, VIP events, prize pool, and lifetime price lock.
            </p>
          </div>

          <div className="fmGrid">
            <div className="fmCard">
              <div className="fmCard__top">
                <span className="fmChip">#1</span>
                <span className="fmCard__title">Lifetime Price Lock</span>
              </div>
              <p className="fmCard__p">
                Your rate today is your rate forever — locked at 2026 pricing as long as you remain active.
                Over 10 years, Founding Members save $7,000+ vs joining later.
              </p>
            </div>

            <div className="fmCard">
              <div className="fmCard__top">
                <span className="fmChip">#2</span>
                <span className="fmCard__title">Annual VIP Event</span>
              </div>
              <p className="fmCard__p">
                Invitation-only event every year for the Founding Members.
              </p>
            </div>

            <div className="fmCard">
              <div className="fmCard__top">
                <span className="fmChip">#3</span>
                <span className="fmCard__title">MVP Program</span>
              </div>
              <p className="fmCard__p">
               Prizes, Upgrades, Bonuses, Gear Discounts, and other perks.
              </p>
            </div>

            <div className="fmCard">
              <div className="fmCard__top">
                <span className="fmChip">#4</span>
                <span className="fmCard__title">Priority Access</span>
              </div>
              <p className="fmCard__p">
                Priority waitlists, Elevated booking privileges,
                VIP concierge line, upgrades and add-ons.
              </p>
            </div>

            <div className="fmCard">
              <div className="fmCard__top">
                <span className="fmChip">#5</span>
                <span className="fmCard__title">First Access to What’s Next</span>
              </div>
              <p className="fmCard__p">
                Private unveilings, first bookings on new units, voting rights on fleet expansions,
                early access to new locations and beta programs.
              </p>
            </div>

            <div className="fmCard fmCard--glow">
              <div className="fmCard__top">
                <span className="fmChip">FOREVER</span>
                <span className="fmCard__title">Permanent Recognition</span>
              </div>
              <p className="fmCard__p">
                Founding badge on your profile,
                You’re part of the origin story.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* TIERS / PRICING */}
      <section className="fmSection" id="tiers">
        <div className="fmWrap">
          <div className="fmSection__head">
            <h3 className="fmH3">Founding Member status doesn’t cost extra.</h3>
            <p className="fmP">
              You simply choose your membership tier — and you’re in. The difference is what you lock in for life:
              pricing, perks, and permanent status.
            </p>
          </div>

          <div className="fmTierRow">
  {TIERS.map((t) => (
    <div key={t.name} className={`fmTier ${t.featured ? "isFeatured" : ""}`}>
      <div className="fmTier__head">
        <div className="fmTier__name">{t.name}</div>
        {t.featured && <div className="fmTier__tag">Most Popular</div>}
      </div>

      <div className="fmTier__price">{t.price} / year</div>

      <div className="fmTier__meta">
        <div><span className="fmMuted">Days:</span> {t.days}</div>
        <div><span className="fmMuted">Delivery:</span> {t.delivery}</div>
      </div>

      <Link className="fmBtn fmBtn--small" to={t.path}>
        Claim as {t.name}
      </Link>
    </div>
  ))}
</div>

          <div className="fmCallout">
            <div className="fmCallout__title">Why only {TOTAL_SPOTS}?</div>
            <div className="fmCallout__p">
              Because Founding Member status has to mean something. Small enough to be intimate. Large enough to build a
              real community. Once we hit {TOTAL_SPOTS}, the doors close — forever.
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="fmSection">
        <div className="fmWrap">
          <div className="fmSection__head">
            <h3 className="fmH3">What Founding Members are saying</h3>
            <p className="fmP">Real momentum. Real “glad I didn’t wait.”</p>
          </div>

          <div className="fmQuoteGrid">
            {TESTIMONIALS.map((t) => (
              <div key={t.who} className="fmQuote">
                <div className="fmQuote__mark">“</div>
                <p className="fmQuote__text">{t.quote}</p>
                <div className="fmQuote__who">{t.who}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="fmSection fmSection--final">
        <div className="fmWrap">
          <div className="fmFinal">
            <div className="fmFinal__left">
              <GoldSeal />
              <h3 className="fmH3">Founding status is permanent.</h3>
              <p className="fmP">
                The Founding Members Club is limited — and once it’s filled, it’s closed. Forever.
                Future members will love Try Play Go… but they won’t have the lifetime price lock,
                VIP events, prize pool access, first-access perks, or permanent recognition.
              </p>

              <div className="fmFinal__bullets">
                <div className="fmBullet">✓ Lifetime price lock at 2026 rates</div>
                <div className="fmBullet">✓ Annual VIP Founding event</div>
                <div className="fmBullet">✓ Annual prize pool</div>
                <div className="fmBullet">✓ Priority booking + elevated booking class</div>
                <div className="fmBullet">✓ Permanent recognition (1–{TOTAL_SPOTS})</div>
              </div>
            </div>

            <div className="fmFinal__right">
              <div className="fmPanel">
                <div className="fmPanel__label">Claim Founding Status</div>

                <div className="fmPanel__hint">
                  Choose a tier and we’ll confirm details during your orientation session.
                </div>

                <a className="fmBtn" href="#tiers">
                  View Tiers & Claim
                </a>

                <div className="fmPanel__contact">
                  <div className="fmPanel__contactTitle">Questions?</div>
                  <div className="fmTiny">
                    Email: <a href="mailto:founders@tryplaygo.com">sales@tryplaygo.com</a>
                    <br />
                    Phone: <span className="fmMuted">[519-930-5637]</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="fmFoot">
            <span className="fmMuted">Try Play Go</span> • Founding Members Club • Limited to {TOTAL_SPOTS}
          </div>
        </div>
      </section>
    </div>
  );
}